import { Box, Button, IconButton, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { LUDO_STAKE_PRESETS } from './ludoConstants.js';
import './LudoOnlinePanel.css';

export default function LudoOnlinePanel({
  online,
  pendingInvites,
  inviteStake,
  onInviteStakeChange,
  onSendInvite,
  onRespondInvite,
  onOpenLudo,
  onRefresh,
  loading,
  refreshing = false,
  compact = false,
  maxPlayers = null,
  showWhenEmpty = false,
}) {
  const playerCap = maxPlayers ?? online.length;
  const visibleOnline = online.slice(0, playerCap);
  const hiddenCount = Math.max(0, online.length - playerCap);
  const hasPlayers = online.length > 0;
  const hasInvites = pendingInvites.length > 0;

  if (!hasPlayers && !hasInvites && !showWhenEmpty) {
    return null;
  }

  const headerLabel = compact ? 'Ludo · players online' : 'Players online';

  return (
    <Box className={`ludo-online-panel${compact ? ' ludo-online-panel--compact' : ''}`}>
      <Box className="ludo-online-panel__header">
        <Typography className="ludo-online-panel__label ludo-online-panel__label--header">
          {headerLabel}
        </Typography>
        {onRefresh && (
          <IconButton
            size="small"
            className={`ludo-online-panel__refresh${refreshing ? ' ludo-online-panel__refresh--spin' : ''}`}
            onClick={onRefresh}
            disabled={refreshing || loading}
            aria-label="Refresh players"
          >
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {hasInvites && (
        <Box className="ludo-online-panel__invites">
          <Typography className="ludo-online-panel__label">Incoming Ludo invites</Typography>
          {pendingInvites.map((inv) => (
            <Box key={inv.id} className="ludo-online-panel__invite-row">
              <Box>
                <Typography className="ludo-online-panel__name">{inv.from.name}</Typography>
                <Typography className="ludo-online-panel__meta">Wants to play · ₹{inv.stake}</Typography>
              </Box>
              <Box className="ludo-online-panel__invite-actions">
                <Button size="small" variant="contained" disabled={loading} onClick={() => onRespondInvite(inv.id, true)}>
                  Accept
                </Button>
                <Button size="small" variant="outlined" disabled={loading} onClick={() => onRespondInvite(inv.id, false)}>
                  Decline
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {compact && hasPlayers && (
        <Box className="ludo-online-panel__stakes">
          {LUDO_STAKE_PRESETS.map((stake) => (
            <button
              key={stake}
              type="button"
              className={`ludo-online-panel__stake${inviteStake === stake ? ' ludo-online-panel__stake--active' : ''}`}
              onClick={() => onInviteStakeChange?.(stake)}
            >
              ₹{stake}
            </button>
          ))}
        </Box>
      )}

      {!hasPlayers && !hasInvites && showWhenEmpty && (
        <Typography className="ludo-online-panel__empty">No other players online — tap refresh</Typography>
      )}

      {!hasPlayers && !hasInvites && !showWhenEmpty && !compact && (
        <Typography className="ludo-online-panel__empty">No other players online right now</Typography>
      )}

      {visibleOnline.map((player) => (
        <Box key={player.id} className="ludo-online-panel__row">
          <Box>
            <Typography className="ludo-online-panel__name">{player.name}</Typography>
            <Typography className="ludo-online-panel__meta">
              {player.availableStake != null
                ? `Available · ₹${player.availableStake}`
                : compact
                  ? 'Online · open Ludo'
                  : 'In lobby'}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            className="ludo-online-panel__invite-btn"
            disabled={loading}
            onClick={() => onSendInvite(player.id, player.availableStake ?? inviteStake)}
          >
            Invite ₹{player.availableStake ?? inviteStake}
          </Button>
        </Box>
      ))}

      {compact && hiddenCount > 0 && (
        <Box className="ludo-online-panel__more-note">
          <Typography className="ludo-online-panel__more-text">
            +{hiddenCount} more online — open Ludo lobby to see all players and tables
          </Typography>
          {onOpenLudo && (
            <Button size="small" variant="contained" className="ludo-online-panel__more-btn" onClick={onOpenLudo}>
              Open Ludo lobby
            </Button>
          )}
        </Box>
      )}

      {compact && onOpenLudo && hasPlayers && hiddenCount === 0 && (
        <Button size="small" className="ludo-online-panel__open-ludo" onClick={onOpenLudo}>
          Open Ludo lobby →
        </Button>
      )}
    </Box>
  );
}
